import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ListWithDetails from 'components/Common/ListWithDetails';
import PatientBadge from 'components/Patient/PatientBadge';
import PatientUtils from 'services/PatientUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import { PatientImage } from 'components/Common/UserImage';
import DefaultButton from 'components/Utils/NewDefaultButton';
import './style.scss';
import RouteUtils from 'services/RouteUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import StaffSearchModal from 'components/Modals/StaffSearchModal';
import { DefaultCheckbox, DefaultDivider } from 'services/UIUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';
import CPPListElement from 'components/Patient/CPP/CPPListElement';
import Icon from 'components/Utils/NewIcon';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useIsLoading, useNavStack } from 'careand-redux/slices/ui/hooks';
import { useAppSelector } from 'careand-redux/store';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import PerformanceUtils from 'services/PerformanceUtils';

const PhysicianPatients = (props) => {
  const { physician } = props;

  const nav = useNavStack(Stacks.Master);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [searchModal, setSearchModal] = useState(undefined);
  // null = "Family Practice" (default roster). Otherwise a skill key (e.g. 'menopause').
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [filters, setFilters] = useState({
    male: false,
    female: false,
    under18: false,
    between18and65: false,
    over65: false,
    onlySubscribers: true,
    name: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(undefined);

  const physicianId = physician.id;

  const loadingDefault = useIsLoading(api.Patients.getPhysicianPatients);
  const loadingBySkill = useIsLoading(api.Patients.getPhysicianPatientsBySkill);
  const loadingPhysicianPatients = loadingDefault || loadingBySkill;

  const physicians = useAppSelector((state) => state.physicians.byId);
  const ownId = useAppSelector((state) => state.authentication.user.id);
  const user = physicians[ownId];

  const favoritePatients = user?.favorite_patients || DEFAULT_ARRAY;

  const bookingFlowOptions = useAppSelector((state) => state.appointments.options);

  // Skills the physician *has* AND that are referenced by at least one concern in the
  // booking flow. Drives the tab list — "Family Practice" (null) is always first; any
  // matching skill becomes its own tab that fires getPhysicianPatientsBySkill on click.
  const availableSkills = useMemo(() => {
    const physicianSkills = physician.skills || DEFAULT_ARRAY;
    if (physicianSkills.length === 0) return DEFAULT_ARRAY;
    const concernSkills = new Set(
      (bookingFlowOptions || [])
        .flatMap((opt) => opt.concerns || [])
        .flatMap((concern) => concern.skills || []),
    );
    return physicianSkills.filter((skill) => concernSkills.has(skill));
  }, [physician.skills, bookingFlowOptions]);

  // Reset to default tab if the physician changes and the previously-selected skill is
  // no longer available (e.g. navigating from Cody to a physician without Men's Health).
  useEffect(() => {
    if (selectedSkill && !availableSkills.includes(selectedSkill)) {
      setSelectedSkill(null);
    }
  }, [availableSkills, selectedSkill]);

  const getPatients = useCallback(() => {
    setPatients([]); // reset so default/by-skill lists never mix
    if (selectedSkill) {
      dispatch(
        api.Patients.getPhysicianPatientsBySkill({ physicianId, skill: selectedSkill }),
      ).then((result) => {
        if (result !== undefined) setPatients(result);
      });
      return;
    }
    dispatch(api.Patients.getPhysicianPatients({ physicianId })).then((result) => {
      if (result !== undefined) {
        setPatients((prevState) => {
          return [...prevState, ...result];
        });
      }
    });
    if (favoritePatients.length > 0) {
      dispatch(
        api.Patients.getMultiplePatients({ multipleValuesPayload: { values: favoritePatients } }),
      ).then((result) => {
        if (result !== undefined) {
          setPatients((prevState) => {
            return [...prevState, ...result];
          });
        }
      });
    }
  }, [dispatch, physicianId, favoritePatients, selectedSkill]);

  useEffect(() => {
    getPatients();
  }, [getPatients]);

  async function onSearch(patients = []) {
    if (!searchTerm) return;
    setSearching(true);
    let ids = patients.map(({ id }) => id);
    let term = searchTerm.toLowerCase();
    const result = await dispatch(
      api.HealthRecords.searchHealthRecords({
        term,
        physicianId,
        multipleValuesPayload: { values: ids },
      }),
    );
    if (result) {
      setSearchResults({ patients: PerformanceUtils.arrayToObject(patients), result });
    }
    setSearching(false);
  }

  function goToPatientProfile(patient) {
    let route = RouteUtils.getRoute(RouteUtils.routes().patient, {
      ':patientId': patient.id,
    });
    nav.push(route);
  }

  async function changePrimaryPhysician(physician) {
    setSearchModal(undefined);
    setLoading(true);
    let patient = { ...searchModal, primary_physician: physician.id };
    await dispatch(api.Patients.updatePatient({ id: patient.id, patient }), {
      title: 'Primary Practitioner Update',
      successText: 'Update successful!',
    });
    setLoading(false);
    getPatients();
  }

  const filtered = useMemo(() => {
    let result = [...patients];

    if (Object.values(filters).find((v) => !!v)) {
      let filteredGender = [];

      if (!filters.male && !filters.female) filteredGender = [...patients];

      if (filters.male)
        filteredGender = [...filteredGender, ...patients.filter((p) => p.gender === 'Male')];
      if (filters.female)
        filteredGender = [...filteredGender, ...patients.filter((p) => p.gender === 'Female')];

      if (!filters.under18 && !filters.between18and65 && !filters.over65)
        result = [...filteredGender];
      else result = [];

      if (filters.under18)
        result = [...result, ...filteredGender.filter((p) => PatientUtils.getAgeAsNumber(p) < 18)];
      if (filters.between18and65)
        result = [
          ...result,
          ...filteredGender.filter((p) => {
            let age = PatientUtils.getAgeAsNumber(p);
            return age >= 18 && age <= 65;
          }),
        ];
      if (filters.over65)
        result = [...result, ...filteredGender.filter((p) => PatientUtils.getAgeAsNumber(p) > 65)];

      if (filters.onlySubscribers) result = result.filter((patient) => patient.subscriber);
      if (!!filters.name.trim()) {
        let nameFilter = filters.name
          .toLowerCase()
          .split(' ')
          .filter((word) => !!word);

        function matches(searchArray, value = 'N/A') {
          value = value.toLowerCase();
          return !!searchArray.find((str) => value.includes(str));
        }

        result = result.filter((patient) => {
          return (
            matches(nameFilter, patient.first_name) ||
            matches(nameFilter, patient.last_name) ||
            matches(nameFilter, patient.second_name) ||
            matches(nameFilter, patient.preferred_name)
          );
        });
      }
    }

    let favorites = result.filter((p) => favoritePatients?.includes(p.id));
    let nonFavorites = result.filter((p) => !favoritePatients?.includes(p.id));

    return [
      ..._.uniqBy(PerformanceUtils.sortBy(favorites, ['first_name'], false), (p) => p.id),
      ..._.uniqBy(PerformanceUtils.sortBy(nonFavorites, ['first_name'], false), (p) => p.id),
    ];
  }, [patients, filters, favoritePatients]);

  return (
    <div className="physician-patients full-width full-height">
      {searchModal !== undefined && (
        <DefaultModal
          key="physician-search-modal"
          content={
            <StaffSearchModal
              title="Change Primary Practitioner"
              ignoreAdmins={true}
              onSelect={changePrimaryPhysician}
              backButton={() => setSearchModal(undefined)}
            />
          }
          isOpen={searchModal !== undefined}
          onRequestClose={() => setSearchModal(undefined)}
        />
      )}
      <ListWithDetails
        items={filtered}
        addButton={
          <div className="filters">
            {availableSkills.length > 0 && (
              <DefaultSelectField
                label="Skill"
                value={selectedSkill || FAMILY_PRACTICE}
                onChange={(event) => {
                  const next = event.target.value === FAMILY_PRACTICE ? null : event.target.value;
                  // Clear the list in the same batch as the filter change so React never
                  // renders the old (unfiltered) roster against the new "show all" filter —
                  // that intermediate frame is what was freezing the UI.
                  setPatients([]);
                  setSelectedSkill(next);
                  if (next) setFilters((prev) => ({ ...prev, onlySubscribers: false }));
                }}
                width="100%"
                inputElements={[
                  <MenuItem key="default" value={FAMILY_PRACTICE}>
                    Family Practice
                  </MenuItem>,
                  ...availableSkills.map((skill) => (
                    <MenuItem key={skill} value={skill}>
                      {PhysicianUtils.PHYSICIAN_SKILLS[skill] || skill}
                    </MenuItem>
                  )),
                ]}
              />
            )}
            <span className="small-title">Filters</span>
            <div className="row">
              <DefaultCheckbox
                name="male"
                value={filters.male}
                onChange={() => setFilters({ ...filters, male: !filters.male })}
                text="Male"
              />
              <DefaultCheckbox
                name="female"
                value={filters.female}
                onChange={() => setFilters({ ...filters, female: !filters.female })}
                text="Female"
              />
            </div>
            <div className="row">
              <DefaultCheckbox
                name="under18"
                value={filters.under18}
                onChange={() => setFilters({ ...filters, under18: !filters.under18 })}
                text="<18"
              />
              <DefaultCheckbox
                name="between18and65"
                value={filters.between18and65}
                onChange={() => setFilters({ ...filters, between18and65: !filters.between18and65 })}
                text="18-65"
              />
              <DefaultCheckbox
                name="over65"
                value={filters.over65}
                onChange={() => setFilters({ ...filters, over65: !filters.over65 })}
                text=">65"
              />
            </div>
            <div className="row">
              <DefaultCheckbox
                name="onlySubscribers"
                value={filters.onlySubscribers}
                onChange={() =>
                  setFilters({ ...filters, onlySubscribers: !filters.onlySubscribers })
                }
                text="Only Subscribers"
                style={{ width: '100%' }}
              />
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                onSearch(filtered);
              }}
            >
              <DefaultTextField
                name="filterNames"
                label="Filter by name"
                value={filters.name}
                onChange={(event) => setFilters({ ...filters, name: event.target.value })}
              />
              <DefaultTextField
                name="searchTerm"
                label="Search health records"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <DefaultButton
                name="Search"
                style={{ marginTop: '10px', width: '100%' }}
                onClick={() => onSearch(filtered)}
                disabled={!searchTerm}
                loading={searching || loadingPhysicianPatients}
              />
            </form>
            <span className="small-title" style={{ marginTop: '15px' }}>
              Patients ({filtered.length})
            </span>
          </div>
        }
        overrideDetails={
          searchResults ? (
            <SearchResults searchResults={searchResults} setSearchResults={setSearchResults} />
          ) : null
        }
        renderBadge={(patient) => <PatientBadge patient={patient} />}
        renderDetails={(patient) => (
          <PatientDetails
            patient={patient}
            loading={loading}
            goToPatientProfile={goToPatientProfile}
            changePrimaryPhysician={setSearchModal}
          />
        )}
      />
    </div>
  );
};

const KEY_TO_ICON = {
  consultants: 'users-medical',
  lifestyle_factors: 'dumbbell',
  history: 'clock-rotate-left',
  immunizations: 'vial-virus',
  medical_issues: 'book-medical',
  medications: 'prescription-bottle',
  notes: 'note',
  allergies: 'head-side-cough',
  preventative_screenings: 'microscope',
};

const DEFAULT_ARRAY = [];
const DEFAULT_OBJECT = {};
const FAMILY_PRACTICE = 'familyPractice';

const SearchResults = ({ searchResults = DEFAULT_OBJECT, setSearchResults }) => {
  const [number, setNumber] = useState(20);

  function keyToName(key) {
    return key.replace('_', ' ');
  }

  const { result = DEFAULT_ARRAY, patients = DEFAULT_OBJECT } = searchResults;

  const filteredResults = useMemo(() => {
    return [...result].slice(0, number);
  }, [result, number]);

  return (
    <div className="search-results scroll-list">
      <div className="row title-row">
        <Icon src="xmark" className="close-icon" onClick={() => setSearchResults(undefined)} />
        <span className="large-title">Search Results</span>
      </div>
      <div className="results">
        {filteredResults.map((result) => (
          <>
            <PatientBadge patient={patients[result.patient_id]} goToProfile={true} />
            {Object.keys(result)
              .map((key) => {
                if (Array.isArray(result[key])) {
                  return (
                    result[key].length > 0 && (
                      <CPPListElement
                        allowEdit={false}
                        patient={{ id: result.patient_id }}
                        title={keyToName(key)}
                        field={key}
                        icon={KEY_TO_ICON[key]}
                        items={result[key]}
                        setActiveCPPTab={() => {}}
                      />
                    )
                  );
                }
                return undefined;
              })
              .filter((r) => !!r)}
            <DefaultDivider />
          </>
        ))}
        {filteredResults.length < result?.length && (
          <DefaultButton name="Load More" onClick={() => setNumber(number + 20)} />
        )}
      </div>
    </div>
  );
};

const PatientDetails = (props) => {
  const role = useAppSelector((state) => state.authentication.role);
  const isPhysician = role === 'physician';

  return (
    <div className="profile-column">
      <div className="user-information">
        <span className="name">{PatientUtils.getName(props.patient)}</span>
        <div className="photo-wrapper">
          <PatientImage user={props.patient} size={100} />
        </div>
        <div className="bottom-information">
          <span className="gender-age">
            {PatientUtils.getGender(props.patient)}- {PatientUtils.getBirthdate(props.patient)}
          </span>
          <span className="ohip">OHIP: {PatientUtils.getOhipNumber(props.patient)}</span>
        </div>
        <DefaultButton
          name="View Profile"
          className="blue"
          disabled={props.loading}
          onClick={() => props.goToPatientProfile(props.patient)}
          style={{ width: '250px', marginTop: '27px' }}
        />
        {isPhysician && (
          <>
            <br />
            <FavoritePatientButton patient={props.patient} />
          </>
        )}
        <br />
        <DefaultButton
          name="Change Primary Practitioner"
          loading={props.loading}
          onClick={() => props.changePrimaryPhysician(props.patient)}
        />
      </div>
    </div>
  );
};

export const FavoritePatientButton = ({ patient }) => {
  const dispatch = useAppDispatch();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const ownId = useAppSelector((state) => state.authentication.user.id);
  const user = physicians[ownId];

  const isFavorite = (user?.favorite_patients || []).includes(patient?.id);

  const isLoading = useIsLoading(api.Physicians.updatePhysician);

  async function onFavoriteClick() {
    let physician = { ...user, favorite_patients: user.favorite_patients || [] };
    if (isFavorite) {
      physician.favorite_patients = physician.favorite_patients.filter((id) => id !== patient.id);
    } else {
      physician.favorite_patients = [...physician.favorite_patients, patient.id];
    }

    await dispatch(
      api.Physicians.updatePhysician({
        id: physician.id,
        physician,
      }),
      {
        errorTitle: 'Favorite Patient',
      },
    );
  }

  const title = isFavorite ? 'Remove from Favorites' : 'Add to Favorites';

  return (
    <DefaultButton
      name={title}
      icon={[isFavorite ? 'fas' : 'far', 'star']}
      onClick={onFavoriteClick}
      style={{ width: '250px' }}
      loading={isLoading}
    />
  );
};

PhysicianPatients.propTypes = {
  physician: PropTypes.object.isRequired,
};

export default PhysicianPatients;
